import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useSellPackage,
  useSellPartnerProject,
} from "@/modules/packages/presentation/api/queries";
import {
  useSellPrivateLessonGroupPackage,
  useSellPrivateLessonPrivateSharedPackage,
} from "@/modules/private-lessons/presentation/api/queries";
import { SellPrivateLessonGroupPackageForm } from "@/modules/private-lessons/presentation/ui/SellPrivateLessonGroupPackageForm";
import { SellPrivateLessonPrivateSharedPackageForm } from "@/modules/private-lessons/presentation/ui/SellPrivateLessonPrivateSharedPackageForm";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { campRegistrationsApi } from "@/modules/camp-registrations/presentation/api/client";
import { SellPartnerOffSiteForm } from "@/modules/shared/presentation/ui/SellPartnerOffSiteForm";
import { SellPackageForm } from "@/shared/ui/SellPackageForm";
import { Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2Icon, UsersIcon, UsersRoundIcon } from "lucide-react";
import { useState } from "react";

type PackageType =
  | "package"
  | "private-lesson-group"
  | "private-lesson-shared"
  | "partner-off-site";

const sellTypeTabTriggerClass =
  "flex h-auto min-h-0 flex-col items-stretch gap-0 rounded-lg border px-3 py-2 text-left shadow-none ring-offset-background transition-colors data-[state=active]:border-red-600 data-[state=inactive]:border-border data-[state=active]:bg-muted/50 data-[state=inactive]:bg-card data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:hover:bg-muted/30 data-[state=active]:[&_.sell-type-icon]:text-red-600";

function resolvePackageType(type: string | undefined): PackageType {
  if (type === "private-lesson-group") return "private-lesson-group";
  if (type === "private-lesson-shared") return "private-lesson-shared";
  if (type === "partner-off-site") return "partner-off-site";
  return "package";
}

function SellPackagePage() {
  const nav = useNavigate({ from: "/app/sell" });
  const search = useSearch({ from: "/app/sell" });

  const initialType = resolvePackageType(search.type);
  const [packageType, setPackageType] = useState<PackageType>(initialType);

  const sellPackageMutation = useSellPackage();
  const sellPrivateLessonGroupMutation = useSellPrivateLessonGroupPackage();
  const sellPrivateLessonSharedMutation =
    useSellPrivateLessonPrivateSharedPackage();
  const sellPartnerProjectMutation = useSellPartnerProject();

  const pageTitle = "ຂາຍແພັກເກດ";
  const isPartnerMode = packageType === "partner-off-site";

  const handleTypeChange = (type: PackageType) => {
    setPackageType(type);
    nav({
      to: "/app/sell",
      search: { ...search, type },
      replace: true,
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຂາຍແພັກເກດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              {pageTitle}
            </h2>
            <p className="text-muted-foreground">
              {isPartnerMode
                ? "ບັນທຶກໂຄງການ partner ອອກສະຖານທີ່ · ບໍ່ຜູກນັກຮຽນ ຫຼື ແພັກເກດ"
                : "ກຳນົດວັນທີແລະກວດສອບສະຫຼຸບຄຳສັ່ງຊື້ກ່ອນສົ່ງສິ້ນສຸດ."}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <p className="mb-2 font-medium text-muted-foreground text-sm">
            ເລືອກປະເພດກ່ອນກຳນົດລາຍລະອຽດການຂາຍ
          </p>
          <Tabs
            value={packageType}
            onValueChange={(value) => handleTypeChange(value as PackageType)}
            className="mb-6"
          >
            <TabsList className="grid h-auto w-full grid-cols-1 gap-2 bg-transparent p-0 sm:grid-cols-2 xl:grid-cols-4">
              <TabsTrigger value="package" className={sellTypeTabTriggerClass}>
                <span className="flex items-start gap-2">
                  <UsersRoundIcon
                    className="sell-type-icon mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-sm leading-tight">
                      ແພັກເກດຄລາສ໌
                    </span>
                    <span className="mt-0.5 block line-clamp-1 text-muted-foreground text-[11px] leading-tight">
                      ຄລາສ໌ເປັນກຸ່ມ · ຕາມແມ່ແບບ
                    </span>
                  </span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="private-lesson-group"
                className={sellTypeTabTriggerClass}
              >
                <span className="flex items-start gap-2">
                  <UsersIcon
                    className="sell-type-icon mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-sm leading-tight">
                      ແພັກເກດຮຽນກຸ່ມ
                    </span>
                    <span className="mt-0.5 block line-clamp-1 text-muted-foreground text-[11px] leading-tight">
                      ຊົ່ວໂມງຄົງທີ່ · ຫຼາຍຄົນ
                    </span>
                  </span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="private-lesson-shared"
                className={sellTypeTabTriggerClass}
              >
                <span className="flex items-start gap-2">
                  <UsersIcon
                    className="sell-type-icon mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-sm leading-tight">
                      Private Package
                    </span>
                    <span className="mt-0.5 block line-clamp-1 text-muted-foreground text-[11px] leading-tight">
                      tier ຕາມຈຳນວນຄົນ · ຊື້ຊົ່ວໂມງ
                    </span>
                  </span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="partner-off-site"
                className={sellTypeTabTriggerClass}
              >
                <span className="flex items-start gap-2">
                  <Building2Icon
                    className="sell-type-icon mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-sm leading-tight">
                      Partner ອອກສະຖານທີ່
                    </span>
                    <span className="mt-0.5 block line-clamp-1 text-muted-foreground text-[11px] leading-tight">
                      B2B · ບໍ່ຜູກແພັກເກດ
                    </span>
                  </span>
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-6">
          {packageType === "partner-off-site" ? (
            <SellPartnerOffSiteForm
              key="partner-off-site"
              submitting={sellPartnerProjectMutation.isPending}
              onSubmit={async (vals) => {
                try {
                  const result =
                    await sellPartnerProjectMutation.mutateAsync(vals);
                  nav({
                    to: "/app/sell/success",
                    search: {
                      type: "partner-off-site",
                      partnerSaleId: result.id,
                      partnerLabel: result.partnerLabel ?? undefined,
                      partnerOffSiteDistance: result.offSiteDistance,
                      partnerClassLabels: result.classLabels.join("|"),
                      partnerInstructorLabels:
                        result.instructorLabels.join("|"),
                      partnerSoldAt: result.createdAt,
                      partnerSessionsScheduled:
                        result.scheduledSessionsCount !== undefined
                          ? String(result.scheduledSessionsCount)
                          : undefined,
                      startDate: result.startDate,
                      expireDate: result.expireDate,
                      originalPrice: result.originalPrice?.toString() ?? "",
                      discountAmount: result.discountAmount?.toString() || "",
                      finalPrice: result.finalPrice?.toString() ?? "",
                      partnerPricingStatus: result.pricingStatus,
                      discountType: result.discountType || "",
                      discountValue: result.discountValue?.toString() || "",
                      receiptId: result.receiptId || "",
                      settlementMode: result.settlementMode,
                      creditInvoiceId: result.credit?.invoiceId,
                      creditPaid: result.credit
                        ? String(result.credit.paidAmount)
                        : undefined,
                      creditRemaining: result.credit
                        ? String(result.credit.remainingAmount)
                        : undefined,
                      partnerInstallmentCount:
                        result.installmentCount != null
                          ? String(result.installmentCount)
                          : undefined,
                    },
                  });
                } catch {
                  //
                }
              }}
            />
          ) : packageType === "private-lesson-shared" ? (
            <SellPrivateLessonPrivateSharedPackageForm
              key="private-lesson-shared"
              initialTemplateId={search.templateId}
              initialOwnerStudentId={search.studentId}
              submitting={sellPrivateLessonSharedMutation.isPending}
              onSubmit={async (vals) => {
                try {
                  const result =
                    await sellPrivateLessonSharedMutation.mutateAsync(vals);
                  nav({
                    to: "/app/sell/success",
                    search: {
                      type: "private-lesson-shared",
                      packageId: result.id,
                      studentId: result.studentId,
                      templateId: vals.templateId,
                      startDate: result.startDate,
                      expireDate: result.expireDate,
                      originalPrice: result.originalPrice?.toString() || "",
                      discountAmount: result.discountAmount?.toString() || "",
                      finalPrice: result.finalPrice?.toString() || "",
                      discountType: result.discountType || "",
                      discountValue: result.discountValue?.toString() || "",
                      receiptId: result.receiptId || "",
                      settlementMode: vals.settlementMode,
                      creditInvoiceId: result.credit?.invoiceId,
                      creditPaid: result.credit
                        ? String(result.credit.paidAmount)
                        : undefined,
                      creditRemaining: result.credit
                        ? String(result.credit.remainingAmount)
                        : undefined,
                      privateLessonHours: String(result.totalHours),
                      privateLessonGroupSize:
                        "groupSize" in result
                          ? String(result.groupSize)
                          : undefined,
                      privateLessonSessionsScheduled:
                        result.scheduledSessionsCount !== undefined
                          ? String(result.scheduledSessionsCount)
                          : undefined,
                    },
                  });
                } catch {
                  //
                }
              }}
            />
          ) : packageType === "private-lesson-group" ? (
            <SellPrivateLessonGroupPackageForm
              key="private-lesson-group"
              initialTemplateId={search.templateId}
              initialOwnerStudentId={search.studentId}
              submitting={sellPrivateLessonGroupMutation.isPending}
              onSubmit={async (vals) => {
                try {
                  const result =
                    await sellPrivateLessonGroupMutation.mutateAsync(vals);
                  nav({
                    to: "/app/sell/success",
                    search: {
                      type: "private-lesson-group",
                      packageId: result.id,
                      studentId: result.studentId,
                      templateId: vals.templateId,
                      startDate: result.startDate,
                      expireDate: result.expireDate,
                      originalPrice: result.originalPrice?.toString() || "",
                      discountAmount: result.discountAmount?.toString() || "",
                      finalPrice: result.finalPrice?.toString() || "",
                      discountType: result.discountType || "",
                      discountValue: result.discountValue?.toString() || "",
                      receiptId: result.receiptId || "",
                      settlementMode: vals.settlementMode,
                      creditInvoiceId: result.credit?.invoiceId,
                      creditPaid: result.credit
                        ? String(result.credit.paidAmount)
                        : undefined,
                      creditRemaining: result.credit
                        ? String(result.credit.remainingAmount)
                        : undefined,
                      privateLessonHours: String(result.totalHours),
                      privateLessonGroupSize:
                        "groupSize" in result
                          ? String(result.groupSize)
                          : undefined,
                      privateLessonSessionsScheduled:
                        result.scheduledSessionsCount !== undefined
                          ? String(result.scheduledSessionsCount)
                          : undefined,
                    },
                  });
                } catch {
                  //
                }
              }}
            />
          ) : (
            <SellPackageForm
              key="package"
              packageType="package"
              initialStudentId={search.studentId}
              initialTemplateId={search.templateId}
              initialSelectedRuleIds={
                search.selectedRuleIds
                  ? search.selectedRuleIds.split(",").filter(Boolean)
                  : undefined
              }
              onSubmit={async (vals) => {
                try {
                  const result = await sellPackageMutation.mutateAsync({
                    studentId: vals.studentId,
                    templateId: vals.templateId,
                    startDate: vals.startDate,
                    quantity: vals.quantity ?? 1,
                    deactivateExisting: vals.deactivateExisting,
                    selectedRuleIds: vals.selectedRuleIds,
                    discountType: vals.discountType,
                    discountValue: vals.discountValue,
                    settlementMode: vals.settlementMode,
                    amountPaidNow: vals.amountPaidNow,
                  });

                  if (search.campRegistrationId) {
                    await campRegistrationsApi.linkPackage(
                      search.campRegistrationId,
                      result.id,
                    );
                  }

                  const stackedPeriods =
                    result.packages && result.packages.length > 1
                      ? result.packages
                          .map(
                            (p) =>
                              `${p.periodIndex}:${p.startDate}:${p.expireDate}`,
                          )
                          .join(";")
                      : undefined;

                  nav({
                    to: "/app/sell/success",
                    search: {
                      type: "package",
                      packageId: result.id,
                      studentId: result.studentId,
                      templateId: vals.templateId,
                      startDate: result.startDate,
                      expireDate: result.expireDate,
                      originalPrice: result.originalPrice?.toString() || "",
                      discountAmount: result.discountAmount?.toString() || "",
                      finalPrice: result.finalPrice?.toString() || "",
                      discountType: result.discountType || "",
                      discountValue: result.discountValue?.toString() || "",
                      receiptId: result.receiptId || "",
                      settlementMode: vals.settlementMode,
                      creditInvoiceId: result.credit?.invoiceId,
                      creditPaid: result.credit
                        ? String(result.credit.paidAmount)
                        : undefined,
                      creditRemaining: result.credit
                        ? String(result.credit.remainingAmount)
                        : undefined,
                      selectedClassNames:
                        vals.selectedClassNames?.join("|") || undefined,
                      quantity:
                        result.quantity && result.quantity > 1
                          ? String(result.quantity)
                          : undefined,
                      purchaseBatchId: result.purchaseBatchId ?? undefined,
                      stackedPeriods,
                    },
                  });
                } catch {
                  //
                }
              }}
              submitting={sellPackageMutation.isPending}
            />
          )}
        </div>
      </Main>
    </>
  );
}

export { SellPackagePage };
export default SellPackagePage;
